import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import {
  useGetVideosByAgentRating,
  useAdminTestAgentScoring,
  useGetCachedVideos
} from '../../api/graphql_agent_scores';
import {
  AgentScore,
  AgentSpecialization,
  AdminScoringTestResult,
  Video,
  ScoreDirection
} from '../../types/generated/graphql';
import { usePageTitle } from '../../hooks/usePageTitle';
import { getScoreColor, getRatingColor } from '../../utils/scoring';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  IconButton,
  Collapse,
  Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

interface TestResult {
  originalScore: AgentScore;
  newResult?: AdminScoringTestResult;
  loading: boolean;
  error?: string;
}

export default function AgentTuning({ client }: Props) {
  const { aid } = useParams<{ aid: string }>();
  const [agentId, setAgentId] = useState<{ specialization: string; version: string } | null>(null);

  usePageTitle(agentId ? `Admin - Tuning ${agentId.specialization}:${agentId.version}` : 'Admin - Agent Tuning');
  const [allScores, setAllScores] = useState<AgentScore[]>([]);
  const [selectedVideos, setSelectedVideos] = useState<AgentScore[]>([]);
  const [videoData, setVideoData] = useState<{ [key: string]: Video }>({});
  const [testResults, setTestResults] = useState<{ [key: string]: TestResult }>({});
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>({});
  
  // Form state
  const [identity, setIdentity] = useState<string>('');
  const [extraGuidelines, setExtraGuidelines] = useState<string>('');
  
  const { getVideosByRating, data: scoresData, loading: scoresLoading, error: scoresError } = useGetVideosByAgentRating(client);
  const { data: videosData, loading: videosLoading } = useGetCachedVideos(client, selectedVideos.map(s => s.videoId));
  const { adminTestAgentScoring } = useAdminTestAgentScoring(client);

  // Parse agent ID from URL parameter (format: "SPECIALIZATION:VERSION")
  useEffect(() => {
    if (aid) {
      const [specialization, version] = aid.split(':');
      if (specialization && version) {
        setAgentId({ specialization, version });
      }
    }
  }, [aid]);

  // Fetch scores when agent ID is available
  useEffect(() => {
    if (agentId) {
      getVideosByRating({
        variables: {
          agent: {
            specialization: agentId.specialization as AgentSpecialization,
            version: agentId.version
          },
          limit: 5000
        }
      });
    }
  }, [agentId, getVideosByRating]);

  // Process scores and select sample videos
  useEffect(() => {
    if (scoresData?.getScoresByAgent) {
      const scores = scoresData.getScoresByAgent;
      setAllScores(scores);
      
      console.log('=== AGENT TUNING DEBUG ===');
      console.log(`Total scores: ${scores.length}`);
      const byRating = {
        harmful: scores.filter(s => s.rating === 'HARMFUL').length,
        neutral: scores.filter(s => s.rating === 'NEUTRAL').length,
        beneficial: scores.filter(s => s.rating === 'BENEFICIAL').length
      };
      console.log(`By rating - Harmful: ${byRating.harmful}, Neutral: ${byRating.neutral}, Beneficial: ${byRating.beneficial}`);
      
      // Sort ALL scores from worst to best considering both rating and score
      // Rating priority: HARMFUL > NEUTRAL > BENEFICIAL (for worst-to-best ordering)
      const ratingPriority = { 'HARMFUL': 0, 'NEUTRAL': 1, 'BENEFICIAL': 2 };
      
      const sortedScores = [...scores].sort((a, b) => {
        // First sort by rating (harmful is worst)
        const ratingDiff = ratingPriority[a.rating] - ratingPriority[b.rating];
        if (ratingDiff !== 0) return ratingDiff;
        
        // Then by score within the same rating
        // scoreDirection determines if lower or higher scores are worse
        return a.scoreDirection === ScoreDirection.HigherIsBetter ? a.score - b.score : b.score - a.score;
      });
      
      // Now select videos using percentile-based distribution
      const totalVideos = sortedScores.length;
      const selectedMap = new Map<string, AgentScore>();
      
      if (totalVideos > 0) {
        const targetCount = 30;
        const numPercentiles = 5; // Divide into 5 percentiles (0-20%, 20-40%, etc.)
        const videosPerPercentile = 6; // Try to get 6 videos from each percentile
        
        for (let p = 0; p < numPercentiles; p++) {
          const percentileStart = Math.floor((p * totalVideos) / numPercentiles);
          const percentileEnd = Math.floor(((p + 1) * totalVideos) / numPercentiles);
          const percentileVideos = sortedScores.slice(percentileStart, percentileEnd);
          
          if (percentileVideos.length === 0) continue;
          
          // If percentile has fewer videos than desired, take all
          if (percentileVideos.length <= videosPerPercentile) {
            percentileVideos.forEach(score => {
              if (selectedMap.size < targetCount) {
                selectedMap.set(score.id, score);
              }
            });
          } else {
            // Otherwise, take evenly distributed samples from this percentile
            const step = Math.floor(percentileVideos.length / videosPerPercentile);
            for (let i = 0; i < videosPerPercentile; i++) {
              const index = Math.min(i * step, percentileVideos.length - 1);
              const score = percentileVideos[index];
              if (score && selectedMap.size < targetCount) {
                selectedMap.set(score.id, score);
              }
            }
          }
        }
        
        // If we have fewer than target, add more from the extremes
        if (selectedMap.size < targetCount) {
          // Add more worst videos
          for (let i = 0; i < sortedScores.length && selectedMap.size < targetCount; i++) {
            selectedMap.set(sortedScores[i].id, sortedScores[i]);
          }
        }
      }
      
      const selected = Array.from(selectedMap.values());
      
      // Keep them in worst-to-best order for display
      selected.sort((a, b) => {
        const ratingDiff = ratingPriority[a.rating] - ratingPriority[b.rating];
        if (ratingDiff !== 0) return ratingDiff;
        return a.scoreDirection === ScoreDirection.HigherIsBetter ? a.score - b.score : b.score - a.score;
      });
      
      console.log('Selected videos for tuning:');
      console.log(`Count: ${selected.length} (target: 30)`);
      console.log('Percentile distribution:');
      const percentiles = [0, 20, 40, 60, 80, 100];
      percentiles.forEach((p, i) => {
        if (i === 0) return;
        const start = Math.floor(((i-1) * selected.length) / 5);
        const end = Math.floor((i * selected.length) / 5);
        const percentileSelection = selected.slice(start, end);
        if (percentileSelection.length > 0) {
          console.log(`  ${percentiles[i-1]}-${p}%: ${percentileSelection.length} videos, scores ${percentileSelection[0].score.toFixed(1)}-${percentileSelection[percentileSelection.length-1]?.score.toFixed(1) || percentileSelection[0].score.toFixed(1)}`);
        }
      });
      console.log('=== END DEBUG ===');
      
      setSelectedVideos(selected);
      
      // Initialize test results
      const initialResults: { [key: string]: TestResult } = {};
      selected.forEach(score => {
        initialResults[score.id] = {
          originalScore: score,
          loading: false
        };
      });
      setTestResults(initialResults);
    }
  }, [scoresData]);

  // Process video data
  useEffect(() => {
    if (videosData?.getCachedVideos) {
      const videoMap: { [key: string]: Video } = {};
      videosData.getCachedVideos.forEach(video => {
        videoMap[video.id] = video;
      });
      setVideoData(videoMap);
    }
  }, [videosData]);

  const handleTestVideo = async (score: AgentScore) => {
    if (!agentId) return;

    setTestResults(prev => ({
      ...prev,
      [score.id]: { ...prev[score.id], loading: true, error: undefined }
    }));

    try {
      const result = await adminTestAgentScoring({
        variables: {
          videoId: score.videoId,
          agent: {
            specialization: agentId.specialization as AgentSpecialization,
            version: agentId.version
          },
          overrides: (identity || extraGuidelines) ? {
            identity: identity || undefined,
            extraGuidelines: extraGuidelines || undefined
          } : null
        }
      });

      if (result.data?.adminTestAgentScoring) {
        setTestResults(prev => ({
          ...prev,
          [score.id]: {
            ...prev[score.id],
            newResult: result.data!.adminTestAgentScoring,
            loading: false
          }
        }));
      }
    } catch (error) {
      setTestResults(prev => ({
        ...prev,
        [score.id]: {
          ...prev[score.id],
          loading: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        }
      }));
    }
  };

  const handleTestAllVideos = async () => {
    for (const score of selectedVideos) {
      await handleTestVideo(score);
      // Add a small delay to avoid overwhelming the server
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  };

  const handleTestAllVideosParallel = async () => {
    // Set all videos to loading state
    const updatedResults = { ...testResults };
    selectedVideos.forEach(score => {
      updatedResults[score.id] = {
        ...updatedResults[score.id],
        loading: true,
        error: undefined
      };
    });
    setTestResults(updatedResults);

    // Run all tests in parallel
    const promises = selectedVideos.map(score => 
      adminTestAgentScoring({
        variables: {
          videoId: score.videoId,
          agent: {
            specialization: agentId!.specialization as AgentSpecialization,
            version: agentId!.version
          },
          overrides: (identity || extraGuidelines) ? {
            identity: identity || undefined,
            extraGuidelines: extraGuidelines || undefined
          } : null
        }
      }).then(result => ({
        scoreId: score.id,
        result: result.data?.adminTestAgentScoring,
        error: null
      })).catch(error => ({
        scoreId: score.id,
        result: null,
        error: error.message || 'Unknown error'
      }))
    );

    const results = await Promise.allSettled(promises);
    
    // Update results as they complete
    const finalResults = { ...testResults };
    results.forEach((promiseResult) => {
      if (promiseResult.status === 'fulfilled') {
        const { scoreId, result, error } = promiseResult.value;
        finalResults[scoreId] = {
          ...finalResults[scoreId],
          newResult: result || undefined,
          loading: false,
          error: error || undefined
        };
      }
    });
    
    setTestResults(finalResults);
  };


  if (!agentId) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Invalid agent ID format. Expected format: SPECIALIZATION:VERSION
        </Alert>
      </Container>
    );
  }

  if (scoresLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress />
          <Typography variant="h6" sx={{ ml: 2 }}>
            Loading agent scores...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (scoresError) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          Error loading agent scores: {scoresError.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Agent Tuning: {agentId.specialization.replace(/_/g, ' ')} v{agentId.version}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Test parameter overrides on sample videos to tune agent performance.
        </Typography>
      </Box>

      {/* Override Parameters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Override Parameters
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Identity Override (Optional)"
              value={identity}
              onChange={(e) => setIdentity(e.target.value)}
              placeholder="Override the agent's identity/system message..."
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Extra Guidelines (Optional)"
              value={extraGuidelines}
              onChange={(e) => setExtraGuidelines(e.target.value)}
              placeholder="Additional instructions for the agent..."
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleTestAllVideos}
            disabled={selectedVideos.some(s => testResults[s.id]?.loading)}
          >
            Test All (Sequential)
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleTestAllVideosParallel}
            disabled={selectedVideos.some(s => testResults[s.id]?.loading)}
          >
            Test All (Parallel)
          </Button>
        </Box>
      </Paper>

      {/* Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Total Scored
              </Typography>
              <Typography variant="h4">
                {allScores.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Avg Score
              </Typography>
              <Typography variant="h4">
                {allScores.length > 0 ? 
                  (allScores.reduce((sum, s) => sum + s.score, 0) / allScores.length).toFixed(1) : 
                  'N/A'
                }
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Test Sample
              </Typography>
              <Typography variant="h4">
                {selectedVideos.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="text.secondary">
                Distribution
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {selectedVideos.filter(s => s.rating === 'HARMFUL').length > 0 && (
                  <Chip size="small" label={`${selectedVideos.filter(s => s.rating === 'HARMFUL').length} Harmful`} color="error" />
                )}
                {selectedVideos.filter(s => s.rating === 'NEUTRAL').length > 0 && (
                  <Chip size="small" label={`${selectedVideos.filter(s => s.rating === 'NEUTRAL').length} Neutral`} />
                )}
                {selectedVideos.filter(s => s.rating === 'BENEFICIAL').length > 0 && (
                  <Chip size="small" label={`${selectedVideos.filter(s => s.rating === 'BENEFICIAL').length} Beneficial`} color="success" />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Results Table */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Tuning Results
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Video</TableCell>
                <TableCell>Category</TableCell>
                <TableCell align="center">Original</TableCell>
                <TableCell align="center">New Result</TableCell>
                <TableCell align="center">Change</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedVideos.map((score) => {
                const video = videoData[score.videoId];
                const result = testResults[score.id];
                
                // Determine category based on rating and score
                let category = '';
                if (score.rating === 'HARMFUL') {
                  category = 'Harmful';
                } else if (score.rating === 'BENEFICIAL') {
                  category = 'Beneficial';
                } else if (score.score <= 3) {
                  category = 'Low Score';
                } else if (score.score >= 7) {
                  category = 'High Score';
                } else {
                  category = 'Neutral';
                }
                
                const scoreChange = result?.newResult ? 
                  result.newResult.score - score.score : 0;
                
                return (
                  <TableRow key={score.id}>
                    <TableCell>
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            opacity: 0.8
                          }
                        }}
                        onClick={() => window.open(`https://www.youtube.com/watch?v=${score.videoId}`, '_blank')}
                      >
                        {video?.thumbnails?.[0]?.url && (
                          <Box
                            component="img"
                            src={video.thumbnails[0].url}
                            alt=""
                            sx={{ width: 60, height: 40, objectFit: 'cover', borderRadius: 1 }}
                          />
                        )}
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium',
                              textDecoration: 'underline',
                              color: 'primary.main'
                            }}
                          >
                            {video?.title || score.videoId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {video?.author?.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={category} 
                        size="small"
                        color={
                          category === 'Harmful' ? 'error' : 
                          category === 'Beneficial' ? 'success' :
                          category === 'Low Score' ? 'warning' :
                          category === 'High Score' ? 'info' :
                          'default'
                        }
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
                          <Chip
                            label={score.score.toFixed(1)}
                            size="small"
                            sx={{ backgroundColor: getScoreColor(score.score, score.scoreDirection), color: 'white' }}
                          />
                          <Chip
                            label={score.rating}
                            size="small"
                            sx={{ backgroundColor: getRatingColor(score.rating), color: 'white' }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              display: expandedRows[score.id] ? 'block' : 'block',
                              maxWidth: expandedRows[score.id] ? 'none' : '200px',
                              overflow: expandedRows[score.id] ? 'visible' : 'hidden',
                              textOverflow: expandedRows[score.id] ? 'clip' : 'ellipsis',
                              whiteSpace: expandedRows[score.id] ? 'normal' : 'nowrap'
                            }}
                          >
                            {score.explanation}
                          </Typography>
                          <Tooltip title={expandedRows[score.id] ? "Collapse" : "Expand"}>
                            <IconButton 
                              size="small" 
                              onClick={() => setExpandedRows(prev => ({ ...prev, [score.id]: !prev[score.id] }))}
                            >
                              {expandedRows[score.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {result?.loading ? (
                        <CircularProgress size={20} />
                      ) : result?.newResult ? (
                        <Box>
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', mb: 1 }}>
                            <Chip
                              label={result.newResult.score.toFixed(1)}
                              size="small"
                              sx={{ backgroundColor: getScoreColor(result.newResult.score, score.scoreDirection), color: 'white' }}
                            />
                            <Chip
                              label={result.newResult.rating}
                              size="small"
                              sx={{ backgroundColor: getRatingColor(result.newResult.rating), color: 'white' }}
                            />
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                display: expandedRows[score.id] ? 'block' : 'block',
                                maxWidth: expandedRows[score.id] ? 'none' : '200px',
                                overflow: expandedRows[score.id] ? 'visible' : 'hidden',
                                textOverflow: expandedRows[score.id] ? 'clip' : 'ellipsis',
                                whiteSpace: expandedRows[score.id] ? 'normal' : 'nowrap'
                              }}
                            >
                              {result.newResult.explanation}
                            </Typography>
                            <Tooltip title={expandedRows[score.id] ? "Collapse" : "Expand"}>
                              <IconButton 
                                size="small" 
                                onClick={() => setExpandedRows(prev => ({ ...prev, [score.id]: !prev[score.id] }))}
                              >
                                {expandedRows[score.id] ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {result?.newResult ? (
                        <Box>
                          <Typography
                            variant="body2"
                            color={scoreChange > 0 ? 'success.main' : scoreChange < 0 ? 'error.main' : 'text.secondary'}
                            sx={{ fontWeight: 'bold', fontSize: '1.1em' }}
                          >
                            {scoreChange > 0 ? '+' : ''}{scoreChange.toFixed(1)}
                          </Typography>
                          {score.rating !== result.newResult.rating && (
                            <Typography variant="caption" display="block">
                              {score.rating} → {result.newResult.rating}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleTestVideo(score)}
                        disabled={result?.loading}
                      >
                        {result?.loading ? 'Testing...' : 'Test'}
                      </Button>
                      {result?.error && (
                        <Typography variant="caption" color="error" display="block">
                          {result.error}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
}